import { memo } from 'react';

import { type TooltipProps } from '@mui/material';

import { OptionalTooltip } from '@modules/_shared/components/OptionalTooltip';
import { formatTimeAgo } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';

interface TimeAgoProps {
  dateString?: string | null;
  children?: Date | string | null;
  addSuffix?: boolean;
  ignoreTime?: boolean;
  tooltipPlacement?: TooltipProps['placement'];
  shortVersion?: boolean;
  disableTooltip?: boolean;
  label?: string;
  strict?: boolean;
  tooltipTitle?: React.ReactNode;
  toolTipTimeAgo?: boolean;
}

export const TimeAgo = memo(
  ({
    dateString,
    children,
    addSuffix = false,
    ignoreTime = false,
    tooltipPlacement = 'top',
    shortVersion = false,
    disableTooltip = false,
    label,
    strict = true,
    tooltipTitle,
    toolTipTimeAgo = false,
  }: TimeAgoProps) => {
    const { t, dateLocale } = useLocale();
    const effectiveDateString =
      dateString ??
      (children instanceof Date ? children.toISOString() : children ?? null);

    if (!effectiveDateString) {
      return null;
    }

    const { timeAgo, tooltipDate } = formatTimeAgo(
      effectiveDateString,
      addSuffix,
      ignoreTime,
      t,
      dateLocale,
      strict,
      false,
      shortVersion,
    );
    const displayValue = label ?? timeAgo;
    const title =
      tooltipTitle ??
      (toolTipTimeAgo ? `${tooltipDate} ${timeAgo}` : tooltipDate);

    return (
      <OptionalTooltip
        title={title}
        placement={tooltipPlacement}
        arrow={true}
        disabled={disableTooltip}
      >
        <time dateTime={effectiveDateString}>{displayValue}</time>
      </OptionalTooltip>
    );
  },
);
